import { Component } from '@theme/component';
import { compareStore, CompareUpdateEvent, CompareOpenEvent } from '@theme/compare';

/**
 * @typedef {object} Refs
 * @property {HTMLElement} empty
 * @property {HTMLElement} tableWrap
 * @property {HTMLTableElement} table
 *
 * @extends {Component<Refs>}
 */
export class CompareDrawerPanel extends Component {
  requiredRefs = ['empty', 'tableWrap', 'table'];

  /** Cache of fetched product data keyed by handle, cleared on full page load. */
  #cache = new Map();

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CompareOpenEvent.eventName, this.#onOpenRequest);
    document.addEventListener(CompareUpdateEvent.eventName, this.#onUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CompareOpenEvent.eventName, this.#onOpenRequest);
    document.removeEventListener(CompareUpdateEvent.eventName, this.#onUpdate);
  }

  #onOpenRequest = async () => {
    await this.#renderTable(compareStore.list());
    const drawer = document.getElementById('compare-drawer');
    if (drawer && 'open' in drawer && typeof drawer.open === 'function') drawer.open();
  };

  /** @param {CompareUpdateEvent} event */
  #onUpdate = (event) => {
    const drawer = document.getElementById('compare-drawer');
    if (!drawer?.hasAttribute('open')) return;
    this.#renderTable(event.detail.items);
  };

  /** @param {{ id: string | number }} data */
  removeItem(data) {
    compareStore.remove(String(data.id));
  }

  /** @param {import('@theme/compare').CompareItem[]} items */
  async #renderTable(items) {
    if (items.length === 0) {
      this.#showEmpty();
      return;
    }

    const products = (await Promise.all(items.map((item) => this.#getProductData(item)))).filter(
      (product) => product != null
    );

    if (products.length === 0) {
      this.#showEmpty();
      return;
    }

    this.refs.empty.hidden = true;
    this.refs.tableWrap.hidden = false;
    this.#buildTable(products);
  }

  #showEmpty() {
    this.refs.empty.hidden = false;
    this.refs.tableWrap.hidden = true;
  }

  /**
   * @param {import('@theme/compare').CompareItem} item
   * @returns {Promise<any | null>}
   */
  async #getProductData(item) {
    if (this.#cache.has(item.handle)) return this.#cache.get(item.handle);

    try {
      const response = await fetch(`/products/${item.handle}?section_id=compare-drawer-data`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const script = doc.querySelector('script[data-compare-product]');
      if (!script?.textContent) throw new Error('Missing compare data payload');

      const data = JSON.parse(script.textContent);
      this.#cache.set(item.handle, data);
      return data;
    } catch (error) {
      console.error(`[compare-drawer] Failed to load product "${item.handle}"`, error);
      return null;
    }
  }

  /** @param {any[]} products */
  #buildTable(products) {
    const table = this.refs.table;
    table.replaceChildren();
    table.append(this.#buildProductRow(products), this.#buildPriceRow(products), ...this.#buildSpecRows(products));
  }

  /** @param {any[]} products */
  #buildProductRow(products) {
    const row = document.createElement('tr');
    row.append(this.#cell('th', ''));

    for (const product of products) {
      const cell = document.createElement('td');
      cell.className = 'compare-drawer__product-cell';

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'compare-drawer__remove';
      remove.setAttribute('aria-label', `Remove ${product.title} from compare`);
      remove.setAttribute('on:click', `/removeItem?id=${product.id}`);
      remove.textContent = '×';

      const link = document.createElement('a');
      link.href = product.url;

      if (product.image) {
        const img = document.createElement('img');
        img.src = product.image;
        img.alt = product.title;
        img.loading = 'lazy';
        link.append(img);
      }

      const title = document.createElement('span');
      title.className = 'compare-drawer__product-title';
      title.textContent = product.title;
      link.append(title);

      cell.append(remove, link);
      row.append(cell);
    }

    return row;
  }

  /** @param {any[]} products */
  #buildPriceRow(products) {
    const row = document.createElement('tr');
    row.append(this.#cell('th', 'Price'));

    for (const product of products) {
      row.append(this.#cell('td', product.available ? product.price : 'Sold out'));
    }

    return row;
  }

  /** @param {any[]} products */
  #buildSpecRows(products) {
    /** @type {string[]} */
    const labels = [];

    for (const product of products) {
      for (const spec of product.specs ?? []) {
        if (spec.value && !labels.includes(spec.label)) labels.push(spec.label);
      }
    }

    return labels.map((label) => {
      const row = document.createElement('tr');
      row.append(this.#cell('th', label));

      for (const product of products) {
        const spec = (product.specs ?? []).find((s) => s.label === label);
        row.append(this.#cell('td', spec?.value || '—'));
      }

      return row;
    });
  }

  /**
   * @param {'td' | 'th'} tag
   * @param {string} text
   */
  #cell(tag, text) {
    const cell = document.createElement(tag);
    cell.textContent = text;
    return cell;
  }
}

if (!customElements.get('compare-drawer-panel')) {
  customElements.define('compare-drawer-panel', CompareDrawerPanel);
}
